import { useState } from "react"
import { SlidersHorizontal, X, ChevronDown } from "lucide-react"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { DIV_RED } from "@/app/lib/txColors"
import type { ClientTheme } from "@/app/lib/themes"

interface FilterBarProps {
  hasFilters: boolean
  onClear: () => void
  resultCount: number
  totalCount: number
  /** Singular label for the entity being filtered, e.g. "entry", "transaction" */
  entityLabel: string
  theme: ClientTheme
  children: React.ReactNode
}

// The table card's toolbar: label | controls | clear | count on one wrapping
// row. On mobile the Filter label becomes a toggle that collapses the
// controls so the table stays above the fold.
export function FilterBar({
  hasFilters,
  onClear,
  resultCount,
  totalCount,
  entityLabel,
  theme,
  children,
}: FilterBarProps) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const showControls = !isMobile || open

  const entityPlural = entityLabel.endsWith("y")
    ? entityLabel.slice(0, -1) + "ies"
    : entityLabel + "s"

  const label = (
    <>
      <SlidersHorizontal size={14} style={{ color: theme.textMuted }} />
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "12px",
          fontWeight: 500,
          color: theme.textMuted,
        }}
      >
        Filter
      </span>
      {/* Active-filter marker — matters most while collapsed on mobile. */}
      {hasFilters && (
        <span
          className="rounded-full flex-shrink-0"
          style={{ width: "6px", height: "6px", backgroundColor: theme.brandPrimary }}
        />
      )}
    </>
  )

  // Clearing is a destructive-ish reset — use the negative colour language.
  const clearButton = hasFilters && (
    <button
      onClick={onClear}
      className="btn flex items-center gap-1.5 px-3 rounded-lg flex-shrink-0"
      style={{
        height: "34px",
        fontFamily: "var(--font-ui)",
        fontSize: "12px",
        fontWeight: 500,
        color: DIV_RED,
        backgroundColor: `${DIV_RED}12`,
        border: `1px solid ${DIV_RED}30`,
        cursor: "pointer",
      }}
    >
      <X size={12} /> Clear Active Filters
    </button>
  )

  const count = (
    <span
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "12px",
        color: theme.textMuted,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontWeight: 600, color: theme.textPrimary }}>{resultCount}</span>
      {hasFilters ? ` of ${totalCount}` : ""} {entityPlural}
    </span>
  )

  return (
    <div
      className="flex items-center gap-3 px-3.5 py-3 flex-wrap"
      style={{ borderBottom: `1px solid ${theme.borderColor}`, backgroundColor: theme.bgSurface }}
    >
      {isMobile ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 flex-shrink-0"
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
          aria-expanded={open}
        >
          {label}
          <ChevronDown
            size={13}
            style={{
              color: theme.textMuted,
              transform: open ? "rotate(180deg)" : undefined,
              transition: "transform 140ms ease",
            }}
          />
        </button>
      ) : (
        <div className="flex items-center gap-1.5 flex-shrink-0">{label}</div>
      )}
      <div
        style={{
          width: "1px",
          height: "20px",
          backgroundColor: theme.borderColor,
          flexShrink: 0,
        }}
      />
      {showControls && children}
      {showControls && clearButton}
      <div className="ml-auto flex-shrink-0">{count}</div>
    </div>
  )
}
